import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { FaLock, FaTimes, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function ChangePasswordModal({ isOpen, onClose, onSuccess }) {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (newPassword.length < 8) {
      setError('رمز عبور جدید باید حداقل ۸ کاراکتر باشد');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('رمز عبور جدید و تکرار آن یکسان نیستند');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('userToken');

    try {
      await axios.post(
        `${API_URL}/api/user/change-password`,
        {
          old_password: oldPassword,
          new_password: newPassword
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      alert('رمز عبور با موفقیت تغییر یافت');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.detail || 'خطا در تغییر رمز عبور');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />
          
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800 px-6 py-5">
                <div className="flex items-center justify-between text-white">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <FaLock className="text-2xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">تغییر رمز عبور</h3>
                      <p className="text-white/80 text-sm">لطفاً رمز عبور جدید خود را وارد کنید</p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <FaTimes className="text-xl" />
                  </button>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6">
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4 p-4 bg-red-100 dark:bg-red-900/30 border-2 border-red-500 rounded-xl flex items-center gap-3 text-red-900 dark:text-red-300"
                  >
                    <FaExclamationTriangle className="text-xl flex-shrink-0" />
                    <span className="font-semibold text-sm">{error}</span>
                  </motion.div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block mb-2 font-bold text-gray-700 dark:text-gray-300 text-sm">
                      رمز عبور فعلی
                    </label>
                    <input
                      type="password"
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      required
                      className="w-full p-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-800 dark:text-gray-200 transition-all"
                      placeholder="رمز عبور فعلی خود را وارد کنید"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 font-bold text-gray-700 dark:text-gray-300 text-sm">
                      رمز عبور جدید
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                      className="w-full p-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-800 dark:text-gray-200 transition-all"
                      placeholder="حداقل ۸ کاراکتر"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 font-bold text-gray-700 dark:text-gray-300 text-sm">
                      تکرار رمز عبور جدید
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={8}
                      className="w-full p-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-800 dark:text-gray-200 transition-all"
                      placeholder="رمز عبور جدید را دوباره وارد کنید"
                    />
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mt-4">
                  <div className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <FaCheckCircle className="text-primary-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold mb-1">نکات مهم:</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>رمز عبور جدید باید حداقل ۸ کاراکتر باشد</li>
                        <li>از ترکیب حروف و اعداد استفاده کنید</li>
                        <li>رمز عبور خود را در جای امنی نگهداری کنید</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors font-medium disabled:opacity-50"
                  >
                    انصراف
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-bold shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                        در حال تغییر...
                      </span>
                    ) : (
                      'تغییر رمز عبور'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

export default ChangePasswordModal;
